import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import { Plus, Trash2, Save, Eye, Settings, Edit, X } from 'lucide-react';

const FormBuilderContainer = styled.div`
  padding: 2rem;
  background-color: ${props => props.theme.colors.background.primary};
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const FormBuilderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const BuilderPanel = styled(Card)`
  padding: 2rem;
`;

const PreviewPanel = styled(Card)`
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 1.5rem 0;
`;

const FieldTypes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const FieldTypeButton = styled.button`
  padding: 1rem;
  border: 2px dashed ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  background: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary}10;
  }
`;

const FieldTypeLabel = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const FormFields = styled.div`
  margin-bottom: 2rem;
`;

const FormField = styled.div`
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1rem;
  margin-bottom: 1rem;
  background-color: ${props => props.theme.colors.white};
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const FieldLabel = styled.span`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const FieldActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const FieldConfig = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const PreviewForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PreviewField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PreviewLabel = styled.label`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const PreviewInput = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PreviewTextarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PreviewSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FormBuilder = () => {
  const { theme } = useTheme();
  const [formFields, setFormFields] = useState([]);
  const [formTitle, setFormTitle] = useState('Training Registration Form');
  const [formDescription, setFormDescription] = useState('Please fill out this form to register for the training program.');
  const [formType, setFormType] = useState('training'); // 'training' or 'member'
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showFieldModal, setShowFieldModal] = useState(false);

  const disabilityOptions = [
    'Mobility impairment',
    'Visual impairment',
    'Hearing impairment',
    'Speech impairment',
    'Intellectual or learning disability',
    'Psychosocial disability',
    'Chronic illness',
    'Other / Prefer to self-describe'
  ];

  const fieldTypes = [
    { type: 'text', label: 'Text Input', icon: '📝' },
    { type: 'email', label: 'Email', icon: '📧' },
    { type: 'phone', label: 'Phone', icon: '📞' },
    { type: 'textarea', label: 'Text Area', icon: '📄' },
    { type: 'select', label: 'Dropdown', icon: '📋' },
    { type: 'radio', label: 'Radio Buttons', icon: '🔘' },
    { type: 'checkbox', label: 'Checkboxes', icon: '☑️' },
    { type: 'date', label: 'Date Picker', icon: '📅' },
    { type: 'file', label: 'File Upload', icon: '📎' }
  ];

  const formTemplates = {
    training: {
      title: 'Training Registration Form',
      description: 'Please fill out this form to register for the training program.',
      fields: [
        { type: 'text', label: 'Full Name', required: true },
        { type: 'email', label: 'Email Address', required: true },
        { type: 'phone', label: 'Phone Number', required: true },
        { type: 'textarea', label: 'Why are you interested in this training?', required: true },
        { type: 'select', label: 'Experience Level', options: ['Beginner', 'Intermediate', 'Advanced'], required: true }
      ]
    },
    member: {
      title: 'Member Registration Form',
      description: 'Please fill out this form to join the EWHRDN network.',
      fields: [
        { type: 'text', label: 'First Name', required: true },
        { type: 'text', label: 'Last Name', required: true },
        { type: 'email', label: 'Email Address', required: true },
        { type: 'phone', label: 'Phone Number', required: true },
        { type: 'textarea', label: 'Professional Background', required: true },
        { type: 'textarea', label: 'Human Rights Focus Areas', required: true },
        { type: 'select', label: 'Region', options: ['Addis Ababa', 'Oromia', 'Amhara', 'SNNPR', 'Tigray', 'Other'], required: true },
        { type: 'file', label: 'Profile Photo', required: false },
        {
          type: 'radio',
          label: 'Do you identify as having a physical disability?',
          required: false,
          options: ['Yes', 'No']
        },
        {
          type: 'checkbox',
          label: 'Disability Type(s)',
          required: false,
          options: disabilityOptions
        },
        { type: 'file', label: 'Upload CV / Resume', required: false }
      ]
    }
  };

  const loadTemplate = (templateType) => {
    const template = formTemplates[templateType];
    setFormType(templateType);
    setFormTitle(template.title);
    setFormDescription(template.description);
    
    const fields = template.fields.map((field, index) => ({
      id: Date.now() + index,
      type: field.type,
      label: field.label,
      placeholder: '',
      required: field.required || false,
      options: field.options || []
    }));
    
    setFormFields(fields);
  };

  const addField = (fieldType) => {
    const newField = {
      id: Date.now(),
      type: fieldType,
      label: `New ${fieldType} Field`,
      placeholder: '',
      required: false,
      options: fieldType === 'select' || fieldType === 'radio' ? ['Option 1', 'Option 2'] : [],
      validation: {
        minLength: '',
        maxLength: '',
        pattern: ''
      }
    };
    setFormFields([...formFields, newField]);
  };

  const updateField = (fieldId, updates) => {
    setFormFields(fields =>
      fields.map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    );
  };

  const removeField = (fieldId) => {
    setFormFields(fields => fields.filter(field => field.id !== fieldId));
  };

  const editField = (field) => {
    setEditingField(field);
    setShowFieldModal(true);
  };

  const handlePreview = () => {
    setShowPreviewModal(true);
  };

  const renderPreviewField = (field) => {
    const commonProps = {
      placeholder: field.placeholder,
      required: field.required
    };

    switch (field.type) {
      case 'textarea':
        return <PreviewTextarea {...commonProps} />;
      case 'select':
        return (
          <PreviewSelect {...commonProps}>
            <option value="">Select an option</option>
            {field.options.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </PreviewSelect>
        );
      case 'radio':
        return (
          <div>
            {field.options.map((option, index) => (
              <label key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input type="radio" name={`field_${field.id}`} value={option} />
                {option}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <div>
            {field.options.map((option, index) => (
              <label key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input type="checkbox" value={option} />
                {option}
              </label>
            ))}
          </div>
        );
      case 'file':
        return <PreviewInput type="file" {...commonProps} />;
      default:
        return <PreviewInput type={field.type} {...commonProps} />;
    }
  };

  const saveForm = () => {
    const formData = {
      title: formTitle,
      description: formDescription,
      fields: formFields,
      createdAt: new Date().toISOString(),
      id: Date.now()
    };
    
    // In a real app, this would save to a database
    console.log('Saving form:', formData);
    alert('Form saved successfully!');
  };

  return (
    <FormBuilderContainer theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>Form Builder</Title>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Button variant="outline" leftIcon={<Eye size={16} />} onClick={handlePreview}>
            Preview
          </Button>
          <Button variant="primary" leftIcon={<Save size={16} />} onClick={saveForm}>
            Save Form
          </Button>
        </div>
      </Header>

      <FormBuilderGrid theme={theme}>
        <BuilderPanel theme={theme}>
          <SectionTitle theme={theme}>Form Type & Templates</SectionTitle>
          
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Form Type:
            </label>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <Button 
                variant={formType === 'training' ? 'primary' : 'outline'}
                onClick={() => loadTemplate('training')}
              >
                Training Form
              </Button>
              <Button 
                variant={formType === 'member' ? 'primary' : 'outline'}
                onClick={() => loadTemplate('member')}
              >
                Member Form
              </Button>
            </div>
          </div>

          <SectionTitle theme={theme}>Form Configuration</SectionTitle>
          
          <div style={{ marginBottom: '2rem' }}>
            <Input
              label="Form Title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Enter form title"
            />
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <Input
              label="Form Description"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Enter form description"
            />
          </div>

          <SectionTitle>Add Field Types</SectionTitle>
          <FieldTypes>
            {fieldTypes.map((fieldType) => (
              <FieldTypeButton
                key={fieldType.type}
                onClick={() => addField(fieldType.type)}
              >
                <span style={{ fontSize: '1.5rem' }}>{fieldType.icon}</span>
                <FieldTypeLabel>{fieldType.label}</FieldTypeLabel>
              </FieldTypeButton>
            ))}
          </FieldTypes>

          <SectionTitle>Form Fields</SectionTitle>
          <FormFields>
            {formFields.map((field) => (
              <FormField key={field.id}>
                <FieldHeader>
                  <FieldLabel>{field.label}</FieldLabel>
                  <FieldActions>
                    <Button
                      variant="outline"
                      size="small"
                      leftIcon={<Edit size={14} />}
                      onClick={() => editField(field)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      leftIcon={<Trash2 size={14} />}
                      onClick={() => removeField(field.id)}
                    >
                      Remove
                    </Button>
                  </FieldActions>
                </FieldHeader>
                
                <FieldConfig>
                  <Input
                    label="Field Label"
                    value={field.label}
                    onChange={(e) => updateField(field.id, { label: e.target.value })}
                    size="small"
                  />
                  <Input
                    label="Placeholder"
                    value={field.placeholder}
                    onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                    size="small"
                  />
                </FieldConfig>
                
                {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Options (one per line):
                    </label>
                    <textarea
                      value={field.options.join('\n')}
                      onChange={(e) => updateField(field.id, { options: e.target.value.split('\n').filter(opt => opt.trim()) })}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        minHeight: '80px'
                      }}
                    />
                  </div>
                )}
                
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(field.id, { required: e.target.checked })}
                    />
                    Required field
                  </label>
                </div>
              </FormField>
            ))}
          </FormFields>
        </BuilderPanel>

        <PreviewPanel>
          <SectionTitle>Form Preview</SectionTitle>
          <PreviewForm>
            <h3 style={{ margin: '0 0 1rem 0', color: '#666' }}>{formTitle}</h3>
            <p style={{ margin: '0 0 2rem 0', color: '#666' }}>{formDescription}</p>
            
            {formFields.map((field) => (
              <PreviewField key={field.id}>
                <PreviewLabel>
                  {field.label}
                  {field.required && <span style={{ color: 'red' }}> *</span>}
                </PreviewLabel>
                {renderPreviewField(field)}
              </PreviewField>
            ))}
            
            {formFields.length > 0 && (
              <Button variant="primary" style={{ marginTop: '1rem' }}>
                Submit Registration
              </Button>
            )}
          </PreviewForm>
        </PreviewPanel>
      </FormBuilderGrid>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title={`Preview: ${formTitle}`}
        size="large"
      >
        <div style={{ padding: '1rem' }}>
          <p style={{ marginBottom: '2rem', color: '#666' }}>{formDescription}</p>
          <PreviewForm>
            {formFields.map((field) => (
              <PreviewField key={field.id}>
                <PreviewLabel>
                  {field.label}
                  {field.required && <span style={{ color: 'red' }}> *</span>}
                </PreviewLabel>
                {renderPreviewField(field)}
              </PreviewField>
            ))}
            
            {formFields.length > 0 && (
              <Button variant="primary" style={{ marginTop: '1rem' }}>
                Submit {formType === 'member' ? 'Registration' : 'Application'}
              </Button>
            )}
          </PreviewForm>
        </div>
      </Modal>

      {/* Field Edit Modal */}
      <Modal
        isOpen={showFieldModal}
        onClose={() => {
          setShowFieldModal(false);
          setEditingField(null);
        }}
        title="Edit Field"
      >
        {editingField && (
          <div style={{ padding: '1rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <Input
                label="Field Label"
                value={editingField.label}
                onChange={(e) => setEditingField({...editingField, label: e.target.value})}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <Input
                label="Placeholder"
                value={editingField.placeholder}
                onChange={(e) => setEditingField({...editingField, placeholder: e.target.value})}
              />
            </div>
            
            {(editingField.type === 'select' || editingField.type === 'radio' || editingField.type === 'checkbox') && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Options (one per line):
                </label>
                <textarea
                  value={editingField.options.join('\n')}
                  onChange={(e) => setEditingField({...editingField, options: e.target.value.split('\n').filter(opt => opt.trim())})}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    minHeight: '80px'
                  }}
                />
              </div>
            )}
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={editingField.required}
                  onChange={(e) => setEditingField({...editingField, required: e.target.checked})}
                />
                Required field
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                variant="outline"
                onClick={() => {
                  setShowFieldModal(false);
                  setEditingField(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary"
                onClick={() => {
                  updateField(editingField.id, editingField);
                  setShowFieldModal(false);
                  setEditingField(null);
                }}
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </FormBuilderContainer>
  );
};

export default FormBuilder;